import PropTypes from "prop-types";
import { useRef, useState } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import ButtonBase from "@mui/material/ButtonBase";
import CardContent from "@mui/material/CardContent";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";

// project import
import ProfileTab from "./ProfileTab";
import SettingTab from "./SettingTab";
import Avatar from "../../../../../components/@extended/Avatar";
import MainCard from "../../../../../components/MainCard";
import Transitions from "../../../../../components/@extended/Transitions";

// assets
import LogoutOutlined from "@ant-design/icons/LogoutOutlined";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import UserOutlined from "@ant-design/icons/UserOutlined";
import avatar1 from "../../../../../assets/images/users/avatar-1.png";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../../../../../redux/slices/loginSlice";
import { resetRoleRoute } from "../../../../../redux/slices/UserRoute/UserRoleRouteSlice";
import { resetDataAndLogout } from "../../../../../helpers/auth";

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    "aria-controls": `profile-tabpanel-${index}`,
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const iconBackColorOpen = "grey.100";

  const dispatch = useDispatch();

  const user = useSelector((state) => state?.login?.data || {});
  const userInfo = useSelector((state) => state?.users?.userInfo?.[0] || {});

  const hasPendingRegistration =
    userInfo.arbitratorRecord?.some(r => (r.arb_registrarApprovalStatus || "Pending").toLowerCase() === "pending") ||
    userInfo.mediatorRecord?.some(r => (r.med_registrarApprovalStatus || "Pending").toLowerCase() === "pending");

  const currentYear = new Date().getFullYear();
  const hasCurrentYearRegistration =
    userInfo.arbitratorRecord?.some(r => String(r.arb_certificateYear).includes(String(currentYear))) ||
    userInfo.mediatorRecord?.some(r => String(r.med_certificateYear).includes(String(currentYear)));

  const showRenewalBadge = hasPendingRegistration || !hasCurrentYearRegistration;

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: 0.25,
          bgcolor: open ? iconBackColorOpen : "transparent",
          borderRadius: 1,
          "&:hover": { bgcolor: "secondary.lighter" },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2,
          },
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleToggle}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ p: 0.5 }}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              showRenewalBadge ? (
                isHovered ? (
                  <Chip
                    label="Pending Renewal"
                    size="small"
                    color="warning"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      px: 1,
                      backgroundColor: "#ed6c02",
                      color: "#fff",
                      border: '2px solid #fff',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      bgcolor: "#ed6c02",
                      borderRadius: "50%",
                      border: `2px solid ${theme.palette.background.paper}`,
                    }}
                  />
                )
              ) : (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    bgcolor: "#4caf50",
                    borderRadius: "50%",
                    border: `2px solid ${theme.palette.background.paper}`,
                  }}
                />
              )
            }
          >
            <Avatar
              alt="profile user"
              src={user?.profileImage || avatar1}
              size="sm"
            />
          </Badge>
          <Typography variant="subtitle1" sx={{ textTransform: "capitalize" }}>
            {user?.name}
          </Typography>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position="top-right"
            in={open}
            {...TransitionProps}
          >
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: 290,
                minWidth: 240,
                maxWidth: { xs: 250, md: 290 },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item>
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                        >
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            
                            badgeContent={
                              showRenewalBadge ? (
                                <Chip
                                  label="Pending Renewal"
                                  size="small"
                                  color="error"
                                  sx={{
                                    height: 14,
                                    fontSize: "0.5rem",
                                    px: 0.3,
                                    backgroundColor: "#f44336",
                                    color: "#fff",
                                    border: `1px solid ${theme.palette.background.paper}`,
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    bgcolor: "#4caf50",
                                    borderRadius: "50%",
                                    border: `1px solid ${theme.palette.background.paper}`,
                                  }}
                                />
                              )
                            }
                          >
                            <Avatar
                              alt="profile user"
                              src={user?.profileImage || avatar1}
                              sx={{ width: 32, height: 32 }}
                            />
                          </Badge>
                          <Stack>
                            <Typography
                              variant="h6"
                              sx={{ textTransform: "capitalize" }}
                            >
                              {" "}
                              {user?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.userDesignation?.designationName}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item>
                        <Tooltip title="Logout">
                          <IconButton
                            size="large"
                            sx={{ color: "text.primary" }}
                            href="login"
                            onClick={() => {
                              resetDataAndLogout();
                              dispatch(logOut(null), resetRoleRoute());
                            }}
                          >
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                      variant="fullWidth"
                      value={value}
                      onChange={handleChange}
                      aria-label="profile tabs"
                    >
                      {/* <Tab
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                          textTransform: "capitalize",
                        }}
                        icon={
                          <UserOutlined
                            style={{ marginBottom: 0, marginRight: "10px" }}
                          />
                        }
                        label="Profile"
                        {...a11yProps(0)}
                      /> */}
                      <Tab
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "left",
                          alignItems: "center",
                          textTransform: "capitalize",
                        }}
                        icon={
                          <SettingOutlined
                            style={{ marginBottom: 0, marginRight: "10px" }}
                          />
                        }
                        label="Setting"
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab handleClose={handleClose} />
                  </TabPanel>
                  {/* <TabPanel value={value} index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel> */}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number,
  index: PropTypes.number,
  other: PropTypes.any,
};
